import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { type Connecting, Grant, type Selected } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Badge, Button, Callout, Field, Page, PageHeader, Section, Select, useConfirm } from '@beyond-js/ui/react';
import { useQuestions } from '@accounts/ui';
import { listed, Permissions } from './permission';
import { Route } from './route';
import { Summary } from './summary';

/** What the person decided, as `Asking` sends it */
export interface Decision {
	selected: Selected;
	organization?: string;
}

/**
 * The consent's choice (`/connect?ticket=`): who receives access, where it acts, what it already has
 * there apart from what it asks for now, and beside them what the decision leaves it able to do, with
 * Allow and Cancel.
 *
 * Only permissions that read are chosen for the person when the page opens (`Grant.suggested`), and they
 * stay ordinary checkboxes. What it already has is kept unless the person opens **Remove some** and
 * unchecks it; Allow then confirms the removal with its consequence. Allow waits quiet, saying what it
 * needs, until there is something to allow. Each new read of the request (after an access that changed)
 * starts the choice again from what is true now.
 */
export function Choosing({ asked, busy, armed, note, changed, onAllow, onCancel }: {
	asked: Connecting;
	busy?: 'allow' | 'cancel';
	armed: boolean;
	/** A line about Allow itself, such as the sign-in it confirms first */
	note?: ReactNode;
	/** The access changed while the page was open: said once, over the choice read again */
	changed: boolean;
	onAllow: (decision: Decision) => void;
	onCancel: () => void;
}) {
	const { text, language } = usePresence();
	const { confirm } = useConfirm(useQuestions());
	const [organization, setOrganization] = useState('');
	const grant = useMemo(() => new Grant(asked, organization || null), [asked, organization]);
	const [chosen, setChosen] = useState<string[]>(() => grant.suggested);
	const [removing, setRemoving] = useState(false);
	const [removed, setRemoved] = useState<string[]>([]);

	// A request read again, or another organization, starts the choice again from what is true there
	useEffect(() => {
		setChosen(grant.suggested);
		setRemoving(false);
		setRemoved([]);
	}, [grant]);

	const client = asked.client.name;
	const product = asked.product.name;
	const outcome = grant.outcome(chosen, removed);
	const ready = grant.ready(chosen, removed);
	const kept = grant.kept;
	const choice = grant.asked;
	const disabled = busy !== undefined;
	const named = (id: string | null) => (id ? asked.organizations.find(entry => entry.organization === id)?.name ?? text('permissions.overlap.left') : text('connect.organization.all'));
	const where = named(organization || null);
	const fallback = text('permissions.undescribed', { product });

	async function allow() {
		if (outcome.removes.length) {
			const values = { client, product, count: outcome.removes.length, list: listed(outcome.removes, language, fallback), permission: listed(outcome.removes, language, fallback) };
			const accepted = await confirm({
				title: text(outcome.removes.length === 1 ? 'permissions.confirm.one' : 'permissions.confirm.many', values),
				message: text('permissions.confirm.body', values),
				consequence: { affected: text('permissions.confirm.affected'), lost: text('permissions.confirm.lost', values), recovery: text('permissions.confirm.recovery') },
				accept: text('permissions.confirm.allow'),
				cancel: text('action.cancel'),
				tone: 'danger'
			});
			if (!accepted) return;
		}
		onAllow({ selected: grant.selection(chosen, removed), ...(organization ? { organization } : {}) });
	}

	const header = <PageHeader title={text('connect.title', { client })} status={<Badge tone="warning" label={text('connect.unverified')} />} lead={text('connect.lead')} />;
	const actions = (
		<>
			<Button
				variant="primary"
				busy={busy === 'allow'}
				disabled={disabled || !armed}
				waiting={!ready}
				place={() => document.querySelector<HTMLInputElement>('.permission-choice input:not(:disabled)')}
				labels={{ busy: text('connect.allowing') }}
				onClick={() => void allow()}
			>
				{text('connect.allow')}
			</Button>
			<Button variant="secondary" busy={busy === 'cancel'} disabled={disabled} onClick={onCancel}>
				{text('connect.cancel')}
			</Button>
		</>
	);
	const aside = <Summary outcome={outcome} where={where} until={asked.until} product={product} held={kept.length > 0} ready={ready} note={note} actions={actions} />;

	return (
		<Page template="task" width="standard" header={header} aside={aside} label={text('permissions.summary')} raised pinned>
			{changed ? <Callout tone="warning" live title={text('permissions.changed')} body={text('permissions.changed.body')} /> : null}
			<Route asked={asked} />
			<div className="flex flex-col gap-3">
				<Field label={text('connect.organization')} hint={text('connect.organization.hint')}>
					<Select
						value={organization}
						disabled={disabled}
						onChange={(event: { target: { value: string } }) => setOrganization(event.target.value)}
						options={[{ value: '', label: text('connect.organization.all') }, ...asked.organizations.map(entry => ({ value: entry.organization, label: entry.name ?? entry.organization }))]}
					/>
				</Field>
				{grant.others.length ? <Callout tone="info" title={overlap(grant, organization, named, text, language, fallback)} /> : null}
			</div>
			{kept.length ? (
				<Section
					title={text('permissions.held')}
					description={text(removing ? 'permissions.held.removing' : 'permissions.held.lead')}
					actions={
						<Button
							variant="quiet"
							disabled={disabled}
							onClick={() => {
								setRemoving(!removing);
								setRemoved([]);
							}}
						>
							{text(removing ? 'permissions.held.keep' : 'permissions.held.change')}
						</Button>
					}
				>
					<div className="permission-choice">
						<Permissions
							label={text('permissions.held')}
							scopes={kept}
							chosen={kept.map(scope => scope.name).filter(name => !removed.includes(name))}
							onChange={next => setRemoved(kept.map(scope => scope.name).filter(name => !next.includes(name)))}
							product={product}
							disabled={disabled || !removing}
							marks={Object.fromEntries(removed.map(name => [name, 'remove' as const]))}
						/>
					</div>
				</Section>
			) : null}
			<Section
				title={text('permissions.asked')}
				description={choice.length ? text(grant.suggested.length && grant.suggested.length < choice.length ? 'permissions.asked.lead' : 'permissions.asked.choose') : text('permissions.asked.none')}
				actions={choice.length ? <span className="text-small text-ink-muted">{text('permissions.count', { chosen: outcome.adds.length, total: choice.length })}</span> : null}
			>
				{choice.length ? (
					<div className="permission-choice">
						<Permissions
							label={text('permissions.asked')}
							scopes={choice}
							chosen={chosen}
							onChange={setChosen}
							product={product}
							disabled={disabled}
							marks={kept.length ? Object.fromEntries(chosen.map(name => [name, 'add' as const])) : {}}
						/>
					</div>
				) : null}
			</Section>
		</Page>
	);
}

/** What the app keeps through its other grants, said so the person knows this choice leaves it as it is */
function overlap(grant: Grant, organization: string, named: (id: string | null) => string, text: ReturnType<typeof usePresence>['text'], language: string, fallback: string): string {
	if (organization) {
		const all = grant.others.find(entry => entry.organization === null);
		if (all) return text('permissions.overlap.all', { list: listed(all.scopes, language, fallback) });
	}
	const names = grant.others.filter(entry => entry.organization).map(entry => named(entry.organization));
	return text('permissions.overlap.some', { list: new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(names) });
}
